import {
  getBookedCoursesByUserIdAndCourseId,
  getBookedCoursesByUserId,
  getAllThemeTimeTrackingByBookedCourseId,
} from '@/firebase/booked-courses'
import {
  getAllCourses,
  getCalibratedScoresByUserId,
  getCourseThemesSubCollection,
} from '@/firebase/courses-ts'
import {
  CoursePurpose,
  CourseSubject,
  ExamBoards,
  ExamLevel,
  IBookedCourse,
  ICourse,
  ICourseDetails,
  IUser,
} from '@/types/firebase'
import { CourseOverviewTabKey, IGenericError } from '@/types/shared'
import { DEFAULT_MMR } from '@/utils/constants'
import { notify } from '@/utils/index'
import { FC, useCallback, useEffect, useState } from 'react'
import { SelectCoursesModalCard } from './SelectCoursesModalCard'
import { QueryConstraint, where } from 'firebase/firestore'
import { IDeSelectCourseArguments } from '@/types/redux'
import {
  CommonCoursePreviewCardSkeleton,
  CommonCourseSettingsFilter,
  generateExamBoardsSelectOptions,
  generateExamLevelGroupSingleOption,
} from '@/components/ui'
import {
  getCountOfLessonsByCourseId,
  getLessonsByConstraints,
} from '@/firebase/lessons-ts'
import { useDispatch } from 'react-redux'
import { updateUserById } from '@/firebase/users'
import {
  addCourseToSelectedCourses,
  removeCourseFromSelectedCourses,
} from '@/redux/slices/selected-courses'
import { sortBy, uniq } from 'lodash'
import { trackPageViewMixpanel } from '@/utils/mixpanel'
import { ADD_NEW_COURSE_MODAL_PAGE } from '@/utils/mixpanel/pageNames'
import { useSelector } from 'react-redux'
import studentOnboardingTutorialSelector from '@/redux/selectors/student-onboarding-tutorial'
import { StudentOnboardingTutorialStep } from '@/types/redux/student-onboarding-tutorial'
import { twJoin } from 'tailwind-merge'
import {
  v8ExamBoardSelectStyles,
  v8ExamLevelSelectStyles,
  v8SubjectSelectStyles,
} from '@/components/ui/common/selectV8Styles'

interface Props {
  student: IUser
  /**
   * @deprecated Use onSelectionComplete for side effects instead.
   * The modal now dispatches add/remove actions directly to Redux for efficient updates.
   */
  refetchCourses?: () => Promise<void>
  handleCourseSelect: (
    courseDetails: ICourseDetails,
    selectedExamBoard: ExamBoards,
    refetchHandler?: () => Promise<void>,
    startLoadingHandler?: () => void,
    stopLoadingHandler?: () => void,
  ) => Promise<void>
  handleCourseDeSelect: (args: IDeSelectCourseArguments) => Promise<void>
  handleModalClose: () => void
  isOnboardingFlow?: boolean
  onboardingFlow?: () => void
  tabToRedirect?: CourseOverviewTabKey
  /**
   * Optional callback called after a course is selected or deselected.
   * Use this for any additional side effects (e.g., fetching related data).
   * Redux state updates are handled automatically via dispatch.
   */
  onSelectionComplete?: () => void
}

export const SelectCoursesModalContent: FC<Props> = ({
  student,
  refetchCourses,
  handleCourseSelect,
  handleCourseDeSelect,
  handleModalClose,
  isOnboardingFlow = false,
  onboardingFlow = () => {},
  tabToRedirect,
  onSelectionComplete,
}) => {
  // S-15: Escape closes the modal (the wrapper's stopPropagation keeps
  // headlessui from seeing the key), same as the close button.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleModalClose()
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleModalClose])

  // State for raw courses and filtering
  const [allCourses, setAllCourses] = useState<ICourse[]>([])
  const [filteredCourses, setFilteredCourses] = useState<ICourse[]>([])
  const [visibleCount, setVisibleCount] = useState(12)
  // Track if initial filtering has been applied (prevents flash of "no courses" message)
  const [hasAppliedInitialFilter, setHasAppliedInitialFilter] = useState(false)

  // State for details cache
  const [courseDetailsMap, setCourseDetailsMap] = useState<
    Record<string, ICourseDetails>
  >({})
  const [bookedCourses, setBookedCourses] = useState<IBookedCourse[]>([])

  // Loading states
  // Start with true to show skeletons immediately and prevent flash of "no courses" message
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [isLoadingSelectedCourseId, setIsLoadingSelectedCourseId] =
    useState<string>()

  // Filter states
  const [selectedExamBoards, setSelectedExamBoards] = useState<ExamBoards[]>([])
  const [selectedSubject, setSelectedSubject] = useState<CourseSubject>()
  const [selectedExamLevels, setSelectedExamLevels] = useState<
    ExamLevel[] | undefined
  >(
    student.curriculumLevel
      ? generateExamLevelGroupSingleOption([student.curriculumLevel])?.value
      : undefined,
  )

  const dispatch = useDispatch()
  const {
    isActive: isOnboardingTutorialActive,
    currentStep,
    highlightedElementId,
  } = useSelector(studentOnboardingTutorialSelector)

  // 1. Fetch all base courses and booked courses initially
  const fetchBaseData = useCallback(async () => {
    if (!student?.uid) return

    try {
      setIsInitialLoading(true)

      // Fetch booked courses
      const booked = await getBookedCoursesByUserId(student.uid)
      setBookedCourses(booked)

      // Fetch all published basic courses
      const queryConstraints: QueryConstraint[] = [
        where('coursePurpose', '==', CoursePurpose.Basic),
        where('publishAt', '==', true),
      ]
      const courses = await getAllCourses(queryConstraints)
      setAllCourses(courses)
    } catch (error: unknown) {
      console.error(
        'error fetchBaseData :>> ',
        (error as IGenericError)?.message || error,
      )
      if (error && typeof error === 'object' && 'message' in error) {
        notify('error', error.message as string)
      }
    } finally {
      setIsInitialLoading(false)
    }
  }, [student.uid])

  useEffect(() => {
    fetchBaseData()
  }, [fetchBaseData])

  useEffect(() => {
    trackPageViewMixpanel({
      'Page Name': ADD_NEW_COURSE_MODAL_PAGE,
    })
  }, [])

  // 2. Filter courses on client side
  useEffect(() => {
    // Only run filtering if we have courses loaded
    if (allCourses.length === 0) return

    let result = allCourses

    // Filter by Exam Boards
    if (selectedExamBoards && selectedExamBoards.length > 0) {
      result = result.filter((c) =>
        c.examBoards.some((b) => selectedExamBoards.includes(b)),
      )
    }

    // Filter by Exam Levels
    if (selectedExamLevels && selectedExamLevels.length > 0) {
      result = result.filter((c) => selectedExamLevels.includes(c.examLevel))
    }

    // Filter by Subject
    if (selectedSubject) {
      result = result.filter((c) => c.subject === selectedSubject)
    }

    setFilteredCourses(result)
    setVisibleCount(12) // Reset visible count on filter change
    // Mark that we've applied the initial filter
    if (!hasAppliedInitialFilter) {
      setHasAppliedInitialFilter(true)
    }
  }, [
    allCourses,
    selectedExamBoards,
    selectedExamLevels,
    selectedSubject,
    hasAppliedInitialFilter,
  ])

  // 3. Fetch details for visible courses that are missing from cache
  const fetchDetailsForVisible = useCallback(async () => {
    if (!student?.uid) return

    const coursesToLoad = filteredCourses.slice(0, visibleCount)
    const missingDetails = coursesToLoad.filter((c) => !courseDetailsMap[c.id])

    if (missingDetails.length === 0) return

    try {
      setIsLoadingDetails(true)

      const resultPromises = missingDetails.map(async (course) => {
        const targetBookedCourse = bookedCourses.find(
          (b) => b.courseId === course.id,
        )

        const [
          calibratedScores,
          numberOfLessons,
          schemaDto,
          themesTimeTracking = [],
        ] = await Promise.all([
          getCalibratedScoresByUserId(student.uid),
          getCountOfLessonsByCourseId(course.id, [
            where('courseId', '==', course.id),
          ]),
          getCourseThemesSubCollection(course.id),
          ...(!!targetBookedCourse?.id
            ? [getAllThemeTimeTrackingByBookedCourseId(targetBookedCourse.id)]
            : []),
        ])

        const curriculumName = course.examLevel
        const MMR = Math.ceil(
          targetBookedCourse?.MMR ??
            calibratedScores[curriculumName] ??
            DEFAULT_MMR,
        )

        let firstLessonId: string | undefined = undefined
        // get first lesson id only in case student is in onboarding tutorial to handle redirect to first lesson after course selection
        if (isOnboardingTutorialActive && !!schemaDto?.length) {
          const firstTheme = schemaDto?.[0]
          const firstModule = firstTheme?.children?.[0]
          const firstTopic = firstModule?.children?.[0]
          if (!!firstTheme?.id && !!firstModule?.id && !!firstTopic?.id) {
            const lessons = await getLessonsByConstraints([
              where('courseId', '==', course.id),
              where('themeId', '==', firstTheme.id),
              where('moduleId', '==', firstModule.id),
              where('topicId', '==', firstTopic.id),
            ])

            if (!!lessons.length) {
              const firstLesson = sortBy(lessons, 'order')?.[0]

              if (!!firstLesson?.id) firstLessonId = firstLesson.id
            }
          }
        }

        return {
          course,
          bookedCourse: targetBookedCourse || ({} as IBookedCourse),
          numberOfLessons,
          MMR,
          schemaDto,
          themesTimeTracking,
          ...(!!firstLessonId
            ? {
                firstLessonId: firstLessonId,
              }
            : {}),
        } as ICourseDetails
      })

      const newDetails = await Promise.all(resultPromises)

      setCourseDetailsMap((prev) => {
        const next = { ...prev }
        newDetails.forEach((d) => {
          next[d.course.id] = d
        })
        return next
      })
    } catch (error) {
      console.error('Error fetching details', error)
    } finally {
      setIsLoadingDetails(false)
    }
  }, [
    student.uid,
    filteredCourses,
    visibleCount,
    courseDetailsMap,
    bookedCourses,
    isOnboardingTutorialActive,
  ])

  useEffect(() => {
    fetchDetailsForVisible()
  }, [fetchDetailsForVisible])

  // Handlers
  const handleSelection = useCallback(
    async (
      targetCourse: ICourseDetails,
    ): Promise<ICourseDetails | undefined> => {
      if (!student?.uid) return undefined
      try {
        const booked = await getBookedCoursesByUserIdAndCourseId(
          student.uid,
          targetCourse.course.id,
        )
        const targetBooked = booked.find(
          (c) => c.courseId === targetCourse.course.id,
        )
        if (!targetBooked) return undefined

        const updatedCourseDetails: ICourseDetails = {
          ...targetCourse,
          bookedCourse: targetBooked,
        }

        // Update cache
        setCourseDetailsMap((prev) => ({
          ...prev,
          [targetCourse.course.id]: updatedCourseDetails,
        }))

        // Update booked courses list
        setBookedCourses((prev) => {
          const idx = prev.findIndex(
            (b) => b.courseId === targetCourse.course.id,
          )
          if (idx >= 0) {
            const newBooked = [...prev]
            newBooked[idx] = targetBooked
            return newBooked
          }
          return [...prev, targetBooked]
        })

        return updatedCourseDetails
      } catch (error: unknown) {
        console.error('error handleSelection', error)
        notify(
          'error',
          (error as IGenericError)?.message || 'Error updating selection',
        )

        return undefined
      }
    },
    [student.uid],
  )

  const handleExamLevelChange = useCallback(
    async (targetExamLevels?: ExamLevel[]) => {
      try {
        if (!targetExamLevels || targetExamLevels.length === 0) return
        const targetLevel: ExamLevel =
          targetExamLevels.length === 1
            ? targetExamLevels[0]
            : ('' as ExamLevel)
        if (student.curriculumLevel === targetLevel) return

        const updateObj: Partial<IUser> = { curriculumLevel: targetLevel }
        await updateUserById(student.uid, updateObj)
      } catch (error: unknown) {
        console.error('error handleExamLevelChange', error)
        notify(
          'error',
          (error as IGenericError)?.message || 'Error updating level',
        )
      }
    },
    [student.curriculumLevel, student.uid],
  )

  const coursesToRender = filteredCourses.slice(0, visibleCount)
  const hasMore = visibleCount < filteredCourses.length

  return (
    <div className="min-w-[calc(100vw-80px)] lg:min-w-[1200px] max-w-[1200px] h-full overflow-hidden flex flex-col gap-6">
      {/* Header */}
      <div className="mt-2 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-v8-ink">Add a course</h2>
          <p className="text-sm text-v8-mut">
            Pick your subject, level and board
            {filteredCourses.length > 0 &&
              ` - ${filteredCourses.length} courses match`}
            .
          </p>
        </div>

        <CommonCourseSettingsFilter
          containerClassName="w-full flex flex-col sm:flex-row items-stretch justify-start gap-2"
          subjectStyles={v8SubjectSelectStyles}
          examLevelStyles={v8ExamLevelSelectStyles}
          examBoardStyles={v8ExamBoardSelectStyles}
          initialExamLevels={
            selectedExamLevels?.length ? selectedExamLevels : undefined
          }
          examLevelChangeHandler={setSelectedExamLevels}
          isExamLevelSelectLoading={isInitialLoading}
          targetExamLevelChangeHandler={handleExamLevelChange}
          examBoardsChangeHandler={setSelectedExamBoards}
          isExamBoardsSelectLoading={isInitialLoading}
          examBoardsSelectOptions={
            allCourses.length
              ? generateExamBoardsSelectOptions(
                  uniq(allCourses.flatMap((c) => c.examBoards)),
                )
              : undefined
          }
          initialSubject={selectedSubject}
          subjectChangeHandler={setSelectedSubject}
        />
      </div>

      {/* Body */}
      <div className="h-full overflow-hidden no-scrollbar overflow-y-auto pb-4">
        {/* Show skeletons while loading OR while waiting for initial filter to be applied */}
        {isInitialLoading || !hasAppliedInitialFilter ? (
          <ul className="w-full p-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-3 pb-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <li key={i} className="basis-1/2 rounded-v8 shadow-v8-card">
                <CommonCoursePreviewCardSkeleton variant="minimal" />
              </li>
            ))}
          </ul>
        ) : (
          <>
            {/* Only show "no courses" message when filtering is complete but no results */}
            {!filteredCourses.length ? (
              <div className="flex flex-col grow justify-center items-center h-full">
                <p className="w-fit mb-40 font-sans not-italic font-semibold text-center leading-6 text-v8-violet">
                  {`Courses for this curriculum level will be available soon`}
                </p>
              </div>
            ) : (
              <div
                className={twJoin(
                  'flex flex-col gap-4',
                  isOnboardingTutorialActive &&
                    currentStep ===
                      StudentOnboardingTutorialStep.SubjectSelect &&
                    'pointer-events-none',
                )}
              >
                <ul
                  id={
                    isOnboardingTutorialActive &&
                    currentStep === StudentOnboardingTutorialStep.CoursesList
                      ? 'courses-list-container'
                      : undefined
                  }
                  className="w-full p-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-3"
                >
                  {coursesToRender.map((course) => {
                    const details = courseDetailsMap[course.id]
                    const isLoading = course.id === isLoadingSelectedCourseId

                    if (!details) {
                      return (
                        <li
                          key={course.id}
                          className="basis-1/2 rounded-v8 shadow-v8-card"
                        >
                          <CommonCoursePreviewCardSkeleton variant="minimal" />
                        </li>
                      )
                    }

                    const isSelected = !!details.bookedCourse.isSelected

                    return (
                      <SelectCoursesModalCard
                        key={course.id}
                        studentId={student.uid}
                        courseWithDetails={details}
                        handleCourseSelect={async (d, b) => {
                          if (
                            isOnboardingTutorialActive &&
                            currentStep ===
                              StudentOnboardingTutorialStep.CoursesList &&
                            highlightedElementId
                          ) {
                            const element =
                              document.getElementById(highlightedElementId)

                            if (element) {
                              element.click()
                            }
                          }

                          await handleCourseSelect(
                            d,
                            b,
                            async () => {
                              const updatedCourseDetails =
                                await handleSelection(d)
                              if (updatedCourseDetails) {
                                dispatch(
                                  addCourseToSelectedCourses(
                                    updatedCourseDetails,
                                  ),
                                )
                              }
                              onSelectionComplete?.()
                              if (isOnboardingFlow) onboardingFlow()
                            },
                            () => setIsLoadingSelectedCourseId(course.id),
                            () => setIsLoadingSelectedCourseId(undefined),
                          )
                        }}
                        handleCourseDeSelect={async (d) =>
                          await handleCourseDeSelect({
                            userId: student.uid,
                            courseDetails: d,
                            refetchHandler: async () => {
                              await handleSelection(d)
                              dispatch(
                                removeCourseFromSelectedCourses({
                                  courseId: d.course.id,
                                }),
                              )
                              onSelectionComplete?.()
                              if (isOnboardingFlow) onboardingFlow()
                            },
                            startLoadingHandler: () =>
                              setIsLoadingSelectedCourseId(course.id),
                            stopLoadingHandler: () =>
                              setIsLoadingSelectedCourseId(undefined),
                          })
                        }
                        isLoading={isLoading}
                        isSelected={isSelected}
                        handleModalClose={handleModalClose}
                        tabToRedirect={tabToRedirect}
                      />
                    )
                  })}
                </ul>

                {hasMore && (
                  <div className="w-full flex justify-center py-4">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 12)}
                      disabled={isLoadingDetails}
                      className="px-6 py-2 bg-v8-paper hover:bg-v8-line text-v8-ink rounded-full font-medium transition-colors disabled:opacity-50"
                    >
                      {isLoadingDetails ? 'Loading...' : 'Load More Courses'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
